/**
 * This file is responsible for all api calls and data normalization
 */
import type {
  AnyObject,
  Property,
  SearchQuery,
  DBRecord,
  MaybeArray,
  DBRecordTarget,
  InferSchemaTypesWrite,
  RelationTarget,
  RelationOptions,
  PropertyDraft,
  DBRecordCreationOptions,
  OrderDirection,
  CreateTextIndexParams,
  TextIndex,
  TextSearchParams,
  TextSearchReport,
  VectorSearchParams,
  VectorSearchReport
} from '@rushdb/javascript-sdk'

import type { GetUserResponse, User } from '~/features/auth/types'
import type {
  BillingInquiryPayload,
  BillingPortalUrls,
  Catalog,
  CheckoutPayload,
  CheckoutResponse,
  SpendCapPayload,
  SubscriptionAction,
  WorkspaceBillingState
} from '~/features/billing/types'
import type { Connector, ConnectorEvent, CreateConnectorInput } from '~/features/connectors/types'
import type { CreateSavedQueryInput, SavedQuery } from '~/features/saved-queries/types'
import type {
  EmbeddingIndex,
  CreateEmbeddingIndexParams,
  EmbeddingIndexStats
} from '~/features/indexes/types'
import type {
  RelationshipPattern,
  RelationshipPatternsResponse
} from '~/features/relationship-patterns/types'
import type { Project, ProjectStats, WithProjectID } from '~/features/projects/types'
import type { ProjectToken, WorkspaceToken } from '~/features/tokens/types'
import type {
  Workspace,
  WorkspaceUser,
  WorkspaceRole,
  WorkspaceAccessList,
  InviteToWorkspaceDto,
  RevokeAccessDto,
  PendingInvite
} from '~/features/workspaces/types'
import type { SsoConfig, SsoDiscoverResponse, UpsertSsoConfigPayload } from '~/features/sso/types'
import type { GenericApiResponse, Override } from '~/types'

import { DBRecordInstance, DBRecordsArrayInstance } from '@rushdb/javascript-sdk'

import { rushDBInstance } from '~/lib/sdk.ts'
import { $token } from '~/features/auth/stores/token.ts'
import { $currentProjectId } from '~/features/projects/stores/id.ts'
import { $currentWorkspaceId } from '~/features/workspaces/stores/current.ts'
import { BASE_URL } from '~/config.ts'

import { FetchError, fetcher } from './fetcher'
import { parseBillingError } from '~/features/billing/errors'
import type { AcceptedUserInviteDto } from '~/features/workspaces/types'
import { openBillingBlockedDialog } from '~/features/billing/stores/blocked'

type WithInit = {
  init?: RequestInit
}

type RelationshipEndpointQuery = {
  labels?: string[]
  where?: AnyObject
}

type RelationshipSearchQuery = Pick<SearchQuery, 'limit' | 'skip' | 'orderBy'> & {
  where?: AnyObject
  source?: RelationshipEndpointQuery
  target?: RelationshipEndpointQuery
}

type SchemaVectorIndex = {
  id: string
  sourceType: string
  similarityFunction: string
  dimensions: number
  status: string
  modelKey: string
}

export type SchemaProperty = {
  id: string
  name: string
  type: string
  min?: number | string
  max?: number | string
  values?: Array<string | number>
  recordsCount?: number
  vectorIndexes?: SchemaVectorIndex[]
}

export type SchemaItem = {
  label: string
  count: number
  properties: SchemaProperty[]
  relationships: Array<{
    label: string
    type: string
    direction: 'in' | 'out'
    count?: number
  }>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (args: any) => void

export type ApiParams<Method extends AnyFunction> = Parameters<Method>[0]

export type ApiResult<Method extends AnyFunction> = Awaited<ReturnType<Method>>

/**
 * A ranked search (semantic, keyword or hybrid): its records, carrying
 * `__score`, and how the engine ran it.
 */
async function rankedSearch(path: string, params: VectorSearchParams | TextSearchParams, init?: RequestInit) {
  let response: {
    data?: Array<DBRecord>
    total?: number
    meta?: { vectorSearch?: VectorSearchReport; textSearch?: TextSearchReport }
  }
  try {
    response = await fetcher(path, {
      ...init,
      body: JSON.stringify(params),
      method: 'POST',
      transformResponse: false
    })
  } catch (error) {
    // fetcher keeps the parsed error body on `data`; surface the API's error message
    // ("Embedding provider error: …", "No ready text index …") like the SDK does.
    if (error instanceof FetchError) {
      const body = error.data as { message?: unknown } | undefined
      const message =
        typeof body?.message === 'string' && body.message ? body.message
        : Array.isArray(body?.message) ? body.message.join(', ')
        : error.message
      throw new FetchError({ data: { message }, message, response: error.response })
    }
    throw error
  }
  const instances = (response.data ?? []).map((record) => new DBRecordInstance(record))
  const result = new DBRecordsArrayInstance(instances, response.total ?? 0)
  result.vectorSearch = response.meta?.vectorSearch
  result.textSearch = response.meta?.textSearch
  return result
}

export const api = {
  ai: {
    async generateSearchQuery({
      currentQuery,
      init,
      projectId,
      prompt
    }: WithInit & WithProjectID & { prompt: string; currentQuery?: SearchQuery }) {
      return fetcher<{ searchQuery: SearchQuery; warnings?: string[] }>(`/api/v1/ai/search-query`, {
        ...init,
        body: JSON.stringify({ prompt, currentQuery }),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    async getSchema({
      force,
      init,
      labels,
      projectId
    }: WithInit & WithProjectID & { labels?: string[]; force?: boolean }) {
      return fetcher<SchemaItem[]>(`/api/v1/ai/schema`, {
        ...init,
        body: JSON.stringify({ force, labels }),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    async getSchemaMarkdown({
      force,
      init,
      labels,
      projectId
    }: WithInit & WithProjectID & { labels?: string[]; force?: boolean }) {
      return fetcher<string>(`/api/v1/ai/schema/md`, {
        ...init,
        body: JSON.stringify({ force, labels }),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    }
  },
  records: {
    // Goes through the raw fetcher instead of the SDK client so the caller's
    // AbortSignal reaches fetch — superseded semantic searches (prompt changed
    // mid-flight) get aborted instead of piling up in parallel.
    async vectorSearch(params: VectorSearchParams, init?: RequestInit) {
      return rankedSearch(`/api/v1/ai/search`, params, init)
    },
    /** Keyword (BM25) search over a text index; `total` counts every match. */
    async textSearch(params: TextSearchParams, init?: RequestInit) {
      return rankedSearch(`/api/v1/search/text`, params, init)
    },
    async importJson({
      init,
      data,
      label,
      options = {}
    }: WithInit & {
      label?: string
      data: MaybeArray<AnyObject>
      options?: DBRecordCreationOptions
    }) {
      return fetcher<boolean | Array<DBRecord> | { message: string; count: number }>(
        `/api/v1/records/import/json`,
        {
          ...init,
          method: 'POST',
          body: JSON.stringify({
            ...(label?.trim() ? { label: label.trim() } : {}),
            data,
            options
          })
        }
      )
    },
    async importCsv({
      init,
      data,
      label,
      options = {},
      parseConfig
    }: WithInit & {
      label: string
      data: string
      options?: DBRecordCreationOptions
      parseConfig?: {
        delimiter?: string
        header?: boolean
        skipEmptyLines?: boolean | 'greedy'
        dynamicTyping?: boolean
        quoteChar?: string
        escapeChar?: string
        newline?: string
      }
    }) {
      try {
        const recordsAny = rushDBInstance.records as any
        if (typeof recordsAny.importCsv === 'function') {
          const res = await recordsAny.importCsv({ label, data, options, parseConfig })
          return res
        }

        // Fallback: direct REST call if SDK method not present (older bundle)
        const token = $token.get()
        const projectId = $currentProjectId.get()
        const workspaceId = $currentWorkspaceId.get()
        const response = await fetch(`${BASE_URL || ''}/api/v1/records/import/csv`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(projectId ? { 'x-project-id': projectId } : {}),
            ...(workspaceId ? { 'x-workspace-id': workspaceId } : {}),
            ...(init?.headers || {})
          },
          body: JSON.stringify({ label, data, options, parseConfig })
        })
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          ;(console.error || (() => undefined))('CSV import failed', response.status, text)
          throw new Error('CSV import failed')
        }
        return await response.json().catch(() => ({}))
      } catch (e: any) {
        // SDK errors are "<status>" or "<status> <server message>"; the reason may be missing,
        // in which case the dialog falls back to the workspace billing state.
        const billingError = parseBillingError(e)
        if (billingError) {
          openBillingBlockedDialog(billingError.reason)
        }
        console.error('importCsv error', e)
        return {}
      }
    },
    async delete({ init, ...payload }: WithInit & ({ ids: string[] } | SearchQuery)) {
      if ('ids' in payload && payload.ids) {
        return rushDBInstance.records.deleteById(payload.ids)
      } else {
        return rushDBInstance.records.delete(payload as SearchQuery)
      }
    },
    async findById({ id, init }: { id: DBRecord['__id']; init: RequestInit }) {
      return rushDBInstance.records.findById(id)
    },
    async deleteById({ id, init }: { id: DBRecord['__id']; init?: RequestInit }) {
      return rushDBInstance.records.deleteById(id)
    },
    async find(searchQuery: SearchQuery, init: RequestInit) {
      return rushDBInstance.records.find(searchQuery)
    },
    async findOne(searchQuery: Omit<SearchQuery, 'skip' | 'limit'>, init: RequestInit) {
      return rushDBInstance.records.findOne(searchQuery)
    },
    async findUniq(searchQuery: Omit<SearchQuery, 'skip' | 'limit'>, init: RequestInit) {
      return rushDBInstance.records.findUniq(searchQuery)
    },
    async set(
      target: DBRecordTarget,
      label: string,
      data: InferSchemaTypesWrite<any> | Array<PropertyDraft>,
      init: RequestInit
    ) {
      return rushDBInstance.records.set({ target, label, data })
    },
    async update(
      target: DBRecordTarget,
      label: string,
      data: Partial<InferSchemaTypesWrite<any>> | Array<PropertyDraft>,
      init: RequestInit
    ) {
      return rushDBInstance.records.update({ target, label, data })
    },
    async export(query: SearchQuery, init: RequestInit) {
      return rushDBInstance.records.export(query)
    },
    async attach({
      source,
      target,
      options,
      init
    }: {
      source: DBRecordTarget
      target: RelationTarget
      options?: RelationOptions
    } & WithInit) {
      return rushDBInstance.records.attach({ source, target, options })
    },
    async detach({
      source,
      target,
      options,
      init
    }: {
      source: DBRecordTarget
      target: RelationTarget
      options?: RelationOptions
    } & WithInit) {
      return rushDBInstance.records.detach({ source, target, options })
    }
  },
  labels: {
    async find({ searchQuery = {}, init }: { searchQuery?: SearchQuery } & WithInit) {
      return rushDBInstance.labels.find(searchQuery)
    }
  },
  relationships: {
    async find({
      init,
      pagination,
      searchQuery
    }: {
      searchQuery: RelationshipSearchQuery
      pagination?: Pick<SearchQuery, 'limit' | 'skip'>
    } & WithInit) {
      return rushDBInstance.relationships.find({ ...searchQuery, ...pagination })
    }
  },
  relationshipPatterns: {
    async list({ projectId, init }: WithProjectID & WithInit) {
      return fetcher<RelationshipPatternsResponse>(`/api/v1/relationships/patterns`, {
        ...init,
        headers: {
          'x-project-id': projectId
        },
        method: 'GET'
      })
    },
    async analyze({ projectId, init }: WithProjectID & WithInit) {
      return fetcher<{ queued: true }>(`/api/v1/relationships/patterns/analyze`, {
        ...init,
        body: JSON.stringify({}),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    async approve({ projectId, id, init }: WithProjectID & WithInit & { id: string }) {
      return fetcher(`/api/v1/relationships/patterns/${id}/approve`, {
        ...init,
        body: JSON.stringify({}),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    async approveMany({ projectId, ids, init }: WithProjectID & WithInit & { ids: string[] }) {
      return fetcher<RelationshipPattern[]>(`/api/v1/relationships/patterns/approve`, {
        ...init,
        body: JSON.stringify({ ids }),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    async ignore({ projectId, id, init }: WithProjectID & WithInit & { id: string }) {
      return fetcher(`/api/v1/relationships/patterns/${id}/ignore`, {
        ...init,
        body: JSON.stringify({}),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    async delete({
      projectId,
      id,
      deleteExisting,
      init
    }: WithProjectID & WithInit & { id: string; deleteExisting?: boolean }) {
      const params = deleteExisting ? '?deleteExisting=true' : ''
      return fetcher<{ deleted: true }>(`/api/v1/relationships/patterns/${id}${params}`, {
        ...init,
        headers: {
          'x-project-id': projectId
        },
        method: 'DELETE'
      })
    }
  },
  workspaces: {
    workspace({ id }: Pick<Workspace, 'id'>, init: RequestInit): Promise<Workspace> {
      return fetcher<Workspace>(`/api/v1/workspaces/${id}`, init)
    },
    list(init: RequestInit) {
      return fetcher<Workspace[]>(`/api/v1/workspaces`, init)
    },
    create({ init, name }: WithInit & Pick<Workspace, 'name'>) {
      return fetcher<Workspace>(`/api/v1/workspaces`, {
        ...init,
        method: 'POST',
        body: JSON.stringify({ name })
      })
    },
    update(params: Partial<Omit<Workspace, 'id'> & Pick<Workspace, 'id'>>, init: RequestInit) {
      const { id, name, ...body } = params

      return fetcher<Workspace>(`/api/v1/workspaces/${id}`, {
        ...init,
        method: 'PATCH',
        body: JSON.stringify({ name })
      })
    },
    delete(params: Pick<Workspace, 'id'>, init: RequestInit) {
      const { id } = params

      return fetcher<Workspace>(`/api/v1/workspaces/${id}`, {
        ...init,
        method: 'DELETE'
      })
    },
    async inviteUser({
      id,
      email,
      projectIds,
      init
    }: WithInit & Pick<Workspace, 'id'> & InviteToWorkspaceDto) {
      return fetcher<{ message: string }>(`/api/v1/workspaces/${id}/invite`, {
        ...init,
        method: 'POST',
        body: JSON.stringify({ email, projectIds })
      })
    },
    async getAccessList({ id, init }: WithInit & Pick<Workspace, 'id'>) {
      return fetcher<WorkspaceAccessList>(`/api/v1/workspaces/${id}/access-list`, {
        ...init,
        method: 'GET'
      })
    },
    async getUserList({ id, init }: WithInit & Pick<Workspace, 'id'>) {
      return fetcher<WorkspaceUser[]>(`/api/v1/workspaces/${id}/user-list`, {
        ...init,
        method: 'GET'
      })
    },
    async changeMemberRole({
      id,
      userId,
      role,
      init
    }: WithInit & Pick<Workspace, 'id'> & { userId: string; role: WorkspaceRole }) {
      return fetcher<{ message: string }>(`/api/v1/workspaces/${id}/members/${userId}/role`, {
        ...init,
        method: 'PATCH',
        body: JSON.stringify({ role })
      })
    },
    async revokeAccess({ id, userIds, init }: WithInit & Pick<Workspace, 'id'> & RevokeAccessDto) {
      return fetcher<{ message: string }>(`/api/v1/workspaces/${id}/revoke-access`, {
        ...init,
        method: 'PATCH',
        body: JSON.stringify({ userIds })
      })
    },
    async updateAccessList({
      id,
      accessMap,
      init
    }: WithInit & Pick<Workspace, 'id'> & { accessMap: WorkspaceAccessList }) {
      return fetcher<{ message: string }>(`/api/v1/workspaces/${id}/access-list`, {
        ...init,
        method: 'PATCH',
        body: JSON.stringify(accessMap)
      })
    },
    async acceptInvitation({ token, init }: WithInit & { token: string }) {
      return fetcher<AcceptedUserInviteDto>(`/api/v1/workspaces/join-workspace`, {
        ...init,
        method: 'POST',
        body: JSON.stringify({ token })
      })
    },
    async getPendingInvites({ init, id }: WithInit & Pick<Workspace, 'id'>): Promise<PendingInvite[]> {
      return fetcher<PendingInvite[]>(`/api/v1/workspaces/${id}/pending-invites`, {
        ...init,
        method: 'GET'
      })
    },

    async removePendingInvite({
      init,
      id,
      email
    }: WithInit & Pick<Workspace, 'id'> & { email: string }): Promise<{ message: string }> {
      return fetcher<{ message: string }>(`/api/v1/workspaces/${id}/pending-invites`, {
        ...init,
        method: 'PATCH',
        body: JSON.stringify({ email })
      })
    },

    async leaveWorkspace({ id, init }: { id: string; init?: RequestInit }): Promise<{ message: string }> {
      return fetcher<{ message: string }>(`/api/v1/workspaces/${id}/leave-workspace`, {
        ...init,
        body: JSON.stringify({}),
        method: 'PATCH'
      })
    }
  },
  tokens: {
    async list({ projectId }: WithProjectID, init: RequestInit) {
      return fetcher<ProjectToken[]>(`/api/v1/tokens`, init)
    },
    // Workspace-wide listing — every key across the workspace's projects. The fetcher
    // injects the current x-workspace-id header, which scopes this on the backend.
    async listAll(init?: RequestInit) {
      return fetcher<WorkspaceToken[]>(`/api/v1/tokens/all`, { ...init })
    },
    async create({ projectId, init, ...body }: WithProjectID & Partial<ProjectToken> & WithInit) {
      const { noExpire, ...payload } = body
      return fetcher<ProjectToken>(`/api/v1/tokens`, {
        ...init,
        body: JSON.stringify({
          ...payload,
          expiration: noExpire ? '*' : payload.expiration
        }),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    async delete({ init, id, projectId }: WithInit & Pick<ProjectToken, 'id'> & { projectId?: string }) {
      return fetcher<ProjectToken>(`/api/v1/tokens/${id}`, {
        ...init,
        // Deleting from the workspace view: the key may belong to a project other than
        // the one in the global header, so pin the owning project explicitly.
        ...(projectId ? { headers: { 'x-project-id': projectId } } : {}),
        method: 'DELETE'
      })
    }
  },
  connectors: {
    async list({ projectId, init }: WithProjectID & WithInit) {
      return fetcher<Connector[]>(`/api/v1/connectors`, {
        ...init,
        headers: {
          'x-project-id': projectId
        }
      })
    },
    async get({ projectId, id, init }: WithProjectID & WithInit & { id: string }) {
      return fetcher<Connector>(`/api/v1/connectors/${id}`, {
        ...init,
        headers: {
          'x-project-id': projectId
        }
      })
    },
    async events({ projectId, id, init }: WithProjectID & WithInit & { id: string }) {
      const events = await fetcher<ConnectorEvent[]>(`/api/v1/connectors/${id}/events`, {
        ...init,
        headers: {
          'x-project-id': projectId
        }
      })

      return events.map((event) => {
        if (!event.metadata || typeof event.metadata !== 'string') return event

        try {
          return { ...event, metadata: JSON.parse(event.metadata) as Record<string, unknown> }
        } catch {
          return event
        }
      })
    },
    async create({ projectId, init, ...body }: WithProjectID & WithInit & CreateConnectorInput) {
      return fetcher<Connector>(`/api/v1/connectors`, {
        ...init,
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'x-project-id': projectId
        }
      })
    },
    async action({
      projectId,
      id,
      action,
      init
    }: WithProjectID & WithInit & { id: string; action: 'pause' | 'resume' | 'resnapshot' | 'test' }) {
      return fetcher<Connector | { ok: boolean; message: string }>(`/api/v1/connectors/${id}/${action}`, {
        ...init,
        method: 'POST',
        headers: {
          'x-project-id': projectId
        }
      })
    }
  },
  savedQueries: {
    async list({ projectId, init }: WithProjectID & WithInit) {
      return fetcher<SavedQuery[]>(`/api/v1/saved-queries`, {
        ...init,
        headers: {
          'x-project-id': projectId
        }
      })
    },
    async create({ projectId, init, ...body }: WithProjectID & WithInit & CreateSavedQueryInput) {
      return fetcher<SavedQuery>(`/api/v1/saved-queries`, {
        ...init,
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'x-project-id': projectId
        }
      })
    },
    async update({
      projectId,
      id,
      init,
      ...body
    }: WithProjectID & WithInit & { id: string } & Partial<CreateSavedQueryInput>) {
      return fetcher<SavedQuery>(`/api/v1/saved-queries/${id}`, {
        ...init,
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: {
          'x-project-id': projectId
        }
      })
    },
    async delete({ projectId, id, init }: WithProjectID & WithInit & { id: string }) {
      return fetcher<boolean>(`/api/v1/saved-queries/${id}`, {
        ...init,
        method: 'DELETE',
        headers: {
          'x-project-id': projectId
        }
      })
    }
  },
  projects: {
    async list(init: RequestInit) {
      const { data: projects, total } = await fetcher<
        GenericApiResponse<Array<Override<Project, { stats?: string }>>>
      >(`/api/v1/projects`, {
        ...init,
        transformResponse: false
      })

      return {
        data: projects.map<Project>((p) => ({
          ...p,
          stats: JSON.parse(p?.stats ?? '{"records":0, "properties": 0}') as ProjectStats
        })),
        total
      }
    },
    async project({ projectId }: WithProjectID, init: RequestInit) {
      const { data: project } = await fetcher<GenericApiResponse<Project>>(`/api/v1/projects/${projectId}`, {
        ...init,
        transformResponse: false
      })

      return project
    },
    async delete({ init, id }: WithInit & Pick<Project, 'id'>) {
      return fetcher<boolean>(`/api/v1/projects/${id}`, {
        method: 'DELETE',
        ...init
      })
    },
    async update({ init, id, ...body }: WithInit & Partial<Project>) {
      return fetcher<Project>(`/api/v1/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        ...init
      })
    },
    async create({ init, ...body }: WithInit & Partial<Project>) {
      return fetcher<Project>(`/api/v1/projects`, {
        method: 'POST',
        body: JSON.stringify(body),
        ...init
      })
    }
  },
  user: {
    async current(init?: RequestInit) {
      const user = await fetcher<GetUserResponse['data']>(`/api/v1/user`, {
        ...init,
        transformResponse: true
      })

      return { ...user }
    },
    async update({ init, ...body }: { init?: RequestInit } & Partial<User>) {
      return fetcher<GetUserResponse>(`/api/v1/user`, {
        ...init,
        body: JSON.stringify(body),
        method: 'PATCH'
      })
    },
    async delete({ init }: { init?: RequestInit }) {
      return fetcher<GetUserResponse>(`/api/v1/user`, {
        ...init,
        body: JSON.stringify({}),
        method: 'DELETE'
      })
    }
  },
  properties: {
    async find({ searchQuery, init }: { searchQuery: SearchQuery; init: RequestInit }) {
      return rushDBInstance.properties.find(searchQuery)
    },
    async values({
      id,
      searchQuery,
      init
    }: {
      id: Property['id']
      searchQuery: SearchQuery & { query?: string; orderBy?: OrderDirection }
      init: RequestInit
    }) {
      return rushDBInstance.properties.values(id, searchQuery)
    }
  },
  auth: {
    async sendRecoveryLink({ email }: { email?: string } & WithInit) {
      return fetcher(`/api/v1/auth/forgot-password/${email}`, {
        method: 'GET'
      })
    },
    async resendConfirmationLink(id: User['id']) {
      return fetcher(`/api/v1/auth/resend-confirmation-link`, {
        body: JSON.stringify({ id }),
        method: 'POST'
      })
    },
    async resetPassword({
      password,
      token,
      login
    }: { login: string; token: string; password: string } & WithInit) {
      return fetcher<GetUserResponse['data']>(`/api/v1/auth/reset-password`, {
        body: JSON.stringify({ password, token, login }),
        method: 'POST'
      })
    },
    async register({ login, password }: { login?: string; password?: string }) {
      return fetcher<GetUserResponse['data']>(`/api/v1/auth/register`, {
        body: JSON.stringify({ login, password }),
        method: 'POST'
      })
    },
    async ssoDiscover({ email }: { email: string } & WithInit): Promise<SsoDiscoverResponse> {
      return fetcher<SsoDiscoverResponse>(`/api/v1/auth/sso/discover`, {
        body: JSON.stringify({ email }),
        method: 'POST'
      })
    }
  },
  sso: {
    async list({ id, init }: WithInit & { id: string }): Promise<SsoConfig[]> {
      return fetcher<SsoConfig[]>(`/api/v1/workspaces/${id}/sso`, { ...init, method: 'GET' })
    },
    async upsert({ id, payload, init }: WithInit & { id: string; payload: UpsertSsoConfigPayload }) {
      return fetcher<SsoConfig>(`/api/v1/workspaces/${id}/sso`, {
        ...init,
        method: 'PUT',
        body: JSON.stringify(payload)
      })
    },
    async remove({ id, configId, init }: WithInit & { id: string; configId: string }) {
      return fetcher<{ message: string }>(`/api/v1/workspaces/${id}/sso/${configId}`, {
        ...init,
        method: 'DELETE'
      })
    }
  },
  billing: {
    async getCatalog(init?: RequestInit) {
      return fetcher<Catalog>(`/api/v1/billing/catalog`, { ...init, method: 'GET' })
    },
    async getState(init?: RequestInit) {
      return fetcher<WorkspaceBillingState | null>(`/api/v1/billing/state`, { ...init, method: 'GET' })
    },
    async checkout({ init, ...body }: WithInit & CheckoutPayload) {
      return fetcher<CheckoutResponse>(`/api/v1/billing/checkout`, {
        ...init,
        method: 'POST',
        body: JSON.stringify(body)
      })
    },
    async updateSubscription({ init, ...body }: WithInit & SubscriptionAction) {
      return fetcher<WorkspaceBillingState>(`/api/v1/billing/subscription`, {
        ...init,
        method: 'POST',
        body: JSON.stringify(body)
      })
    },
    async setSpendCap({ init, ...body }: WithInit & SpendCapPayload) {
      return fetcher<WorkspaceBillingState>(`/api/v1/billing/spend-cap`, {
        ...init,
        method: 'PUT',
        body: JSON.stringify(body)
      })
    },
    async createPortalSession(init?: RequestInit) {
      return fetcher<BillingPortalUrls>(`/api/v1/billing/portal`, {
        ...init,
        method: 'POST',
        cache: 'no-store',
        body: JSON.stringify({})
      })
    },
    async submitInquiry({ init, ...body }: WithInit & BillingInquiryPayload) {
      return fetcher<{ success: boolean }>(`/api/v1/billing/inquiry`, {
        ...init,
        method: 'POST',
        body: JSON.stringify(body)
      })
    }
  },
  settings: {
    get: ({ init }: WithInit) => {
      return fetcher<{
        selfHosted: boolean
        dashboardUrl: string
        googleOAuthEnabled: boolean
        githubOAuthEnabled: boolean
        embeddingEnabled: boolean
        llmEnabled: boolean
        synxEnabled: boolean
      }>(`/api/v1/settings`, {
        ...init,
        method: 'GET'
      })
    }
  },
  health: {
    // The backend exposes /health outside the /api/v1 prefix and returns the
    // raw `{ status }` payload (no `data` envelope), so skip the transform.
    get: ({ init }: WithInit) =>
      fetcher<{ status: string }>('/health', {
        ...init,
        method: 'GET',
        transformResponse: false
      })
  },
  oauth: {
    async getAuthRequest(id: string) {
      return fetcher<{
        auth_request_id: string
        client_name: string
        scope: string
        resource: string
        expires_at: string
      }>(`/oauth/authorize/request/${id}`, { method: 'GET' })
    },
    async acceptAuthorization({
      authRequestId,
      projectId,
      scope
    }: {
      authRequestId: string
      projectId: string
      scope?: string
    }) {
      return fetcher<{ redirectTo: string }>('/oauth/authorize/accept', {
        method: 'POST',
        body: JSON.stringify({
          auth_request_id: authRequestId,
          project_id: projectId,
          ...(scope !== undefined ? { scope } : {})
        })
      })
    },
    async denyAuthorization(authRequestId: string) {
      return fetcher<{ redirectTo: string }>('/oauth/authorize/deny', {
        method: 'POST',
        body: JSON.stringify({ authRequestId })
      })
    },
    async listConsents() {
      return fetcher<
        Array<{
          id: string
          client_id: string
          client_name: string
          scope: string
          project_id: string
          project_name: string
          resource: string
          created: string
        }>
      >('/oauth/consents', { method: 'GET' })
    },
    async revokeConsent(id: string) {
      return fetcher<void>(`/oauth/consents/${id}`, { method: 'DELETE' })
    }
  },
  textIndexes: {
    async list({ projectId }: WithProjectID, init?: RequestInit) {
      return fetcher<TextIndex[]>(`/api/v1/text-indexes`, {
        ...init,
        headers: { 'x-project-id': projectId },
        method: 'GET'
      })
    },
    async create({ projectId, init, ...body }: WithProjectID & CreateTextIndexParams & WithInit) {
      return fetcher<TextIndex>(`/api/v1/text-indexes`, {
        ...init,
        body: JSON.stringify(body),
        headers: { 'x-project-id': projectId },
        method: 'POST'
      })
    },
    /** The default name a new text index on this label and property would get. */
    async suggestName({
      projectId,
      init,
      ...body
    }: WithProjectID & Pick<CreateTextIndexParams, 'label' | 'property'> & WithInit) {
      return fetcher<{ name: string }>(`/api/v1/text-indexes/name-suggestion`, {
        ...init,
        body: JSON.stringify(body),
        headers: { 'x-project-id': projectId },
        method: 'POST'
      })
    },
    async delete({ init, name, projectId }: WithInit & WithProjectID & { name: string }) {
      return fetcher<{ deleted: boolean }>(`/api/v1/text-indexes/${encodeURIComponent(name)}`, {
        ...init,
        headers: { 'x-project-id': projectId },
        method: 'DELETE'
      })
    }
  },
  indexes: {
    async list({ projectId }: WithProjectID, init?: RequestInit) {
      return fetcher<EmbeddingIndex[]>(`/api/v1/ai/indexes`, {
        ...init,
        headers: {
          'x-project-id': projectId
        },
        method: 'GET'
      })
    },
    async create({ projectId, init, ...body }: WithProjectID & CreateEmbeddingIndexParams & WithInit) {
      return fetcher<EmbeddingIndex>(`/api/v1/ai/indexes`, {
        ...init,
        body: JSON.stringify(body),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    /** The default name a new index on this label and property would get. */
    async suggestName({ projectId, init, ...body }: WithProjectID & CreateEmbeddingIndexParams & WithInit) {
      return fetcher<{ name: string }>(`/api/v1/ai/indexes/name-suggestion`, {
        ...init,
        body: JSON.stringify(body),
        headers: {
          'x-project-id': projectId
        },
        method: 'POST'
      })
    },
    async delete({ init, id }: WithInit & { id: string }) {
      return fetcher<{ deleted: boolean }>(`/api/v1/ai/indexes/${id}`, {
        ...init,
        method: 'DELETE'
      })
    },
    async stats({ init, id, projectId }: WithInit & WithProjectID & { id: string }) {
      return fetcher<EmbeddingIndexStats>(`/api/v1/ai/indexes/${id}/stats`, {
        ...init,
        headers: {
          'x-project-id': projectId
        },
        method: 'GET'
      })
    }
  }
}
